import * as React from 'react';
import moment from 'moment';
import { uniq } from 'lodash';

import FilterView from './filter.view';
import { IEventFilter } from '../../classes/calendar';
import { IEvent } from '../../interfaces/event';
import { upsertEventFilter, removeEventFilter, setCurrentMonth } from '../../redux-state/actions/calendar.actions';
import { ICalendarAppState } from '../../redux-state';
import { CalendarApi } from '../../common/calendar-api';
import { connect } from 'react-redux';
import { generateEventFilter, GetFilterFromQuery } from '../../helpers/filter-by-query'
import { scrollToMonth, removeLoader, getMonthElement } from '../../helpers/scroll-to-month';
import { IOption } from '../../interfaces/option';
import { IMonth } from '../../interfaces/month';

interface IFilterExternalProps {
    /**
     * The property to filter the calendar events by.
     */
    propertyToFilter: keyof IEvent;
    /**
     * Text to represent the option for no selection. ie All Venues, All Production
     */
    noSelectionText: string;
    /**
     * Name of the filter to be labeled on the app
     */
    filterName: string;
}

interface StateToProps {
    hasFilters: boolean;
    eventMonths: IMonth<IEvent>[];
    selectedValue: string;
    /**
     * The furthermost month that has been loaded onto the calendar.
     */
    lastLoadedMonth: moment.Moment;
    /**
     * The last month that can be loaded onto the calendar.
     */
    lastMonthToLoad: moment.Moment;
    currentFilters: Map<string, string>;
    startDate: moment.Moment;
    endDate: moment.Moment;
}

interface DispatchToProps {
    selectFilter: (selectedValue: string, shouldUpdateQuery: boolean) => void;
    goToMonth: (date: moment.Moment) => void;
    getFirstOccurenceDateForFilters: (filters: any, lastMonthToLoad: moment.Moment, lastLoadedMonth: moment.Moment) => void;
}

export type FilterProps = IFilterExternalProps & StateToProps & DispatchToProps;

interface FilterState {
    fromApi: boolean;
    waitingForApi: boolean;
    options: IOption[];
}

export type CombinedFilterViewProps = FilterProps & FilterState;

export class FiltersBase extends React.Component<FilterProps, FilterState> {
    constructor(props: FilterProps) {
        super(props);

        this.state = {
            fromApi: false,
            waitingForApi: true,
            options: []
        };
    }

    componentDidMount() {
        CalendarApi.getFiltersFor(this.props.propertyToFilter, this.props.startDate, this.props.endDate).then(response => {
            this.setState({
                fromApi: true,
                waitingForApi: false,
                options: response
            });

            if (window.location.search.length > 1) {
                GetFilterFromQuery(window.location.search, response, this.props.propertyToFilter,
                    this.props.lastLoadedMonth, this.props.selectFilter, this.props.goToMonth);
            }
        }).catch(err => {
            console.error(err);
        });
    }

    componentDidUpdate(prevProps: FilterProps, prevState: FilterState) {
        if (!this.state.fromApi && !this.state.waitingForApi && !prevProps.lastLoadedMonth.isSame(this.props.lastLoadedMonth)) {
            this.setState({
                options: this.calculateOptions(this.props)
            });
        }
    }


    private calculateOptions(props: FilterProps): IOption[] {
        // flatten events twice because month -> day -> events
        const events = ([] as IEvent[]).concat(...props.eventMonths.map(month => ([] as IEvent[]).concat(...month.events.days.map(day => day.events))));

        if (events.length > 0) {
            if (Array.isArray(events[0][props.propertyToFilter])) {
                // These are all casted as any because it's not specifically known what kind of objects this is expected to support.
                const allOptions = uniq(([] as any[]).concat(...events.map(event => (event[props.propertyToFilter] as any[]))));

                return allOptions.map(option => {
                    return {
                        text: option.toString(),
                        value: option.toString(),
                        firstOccurance: ""
                    }
                });
            }
            else {
                const mappedProperty = uniq(events.map(event => event[props.propertyToFilter]));

                return mappedProperty.map(eventProperty => {
                    return {
                        text: eventProperty.toString(),
                        value: eventProperty.toString(),
                        firstOccurance: ""
                    }
                });
            }
        }

        // If empty
        return [];
    }

    render() {
        return <FilterView {...this.props} {...this.state} />;
    }
}

function mapStateToProps(state: ICalendarAppState, ownProps: IFilterExternalProps): StateToProps {
    // Find the matched filter to extract the selected value.
    let matchedFilter: IEventFilter = null;

    for (let i = 0; i < state.calendar.eventFilter.length; ++i) {
        if (state.calendar.eventFilter[i].key === ownProps.propertyToFilter) {
            matchedFilter = state.calendar.eventFilter[i];
            break;
        }
    }

    const startDate = moment(state.calendar.loadedMonths[0]).startOf('day');
    const endDate = moment(state.calendarSettings.monthsOnCalendar[state.calendarSettings.monthsOnCalendar.length - 1]).endOf('month');

    return {
        hasFilters: state.calendar.eventFilter.length > 0,
        eventMonths: state.calendar.events,
        selectedValue: matchedFilter ? matchedFilter.selectedValue : "",
        lastLoadedMonth: state.calendar.endDate,
        lastMonthToLoad: state.calendarSettings.monthsOnCalendar[state.calendarSettings.monthsOnCalendar.length - 1],
        currentFilters: new Map(state.calendar.eventFilter.map(filter => [filter.key, filter.selectedValue]) as any),
        startDate,
        endDate
    }
}

function mapDispatchToProps(dispatch, ownProps: FilterProps): DispatchToProps {
    return {
        selectFilter: (selectedValue: string, shouldUpdateQuery: boolean) => {
            // If the selected value is null or empty string, remove that filter.
            if (!selectedValue || selectedValue === "") {
                dispatch(removeEventFilter(ownProps.propertyToFilter));
            } else {

                const filter: IEventFilter = {
                    key: ownProps.propertyToFilter,
                    selectedValue: selectedValue,
                    filter: generateEventFilter(ownProps.propertyToFilter, selectedValue)
                }
                dispatch(upsertEventFilter(filter, shouldUpdateQuery));
            }
        },
        goToMonth: (date: moment.Moment) => dispatch(setCurrentMonth(date)),
        getFirstOccurenceDateForFilters: (filters, lastMonthToLoad, lastLoadedMonth) => {
            CalendarApi.getFirstOccurenceForFilters(filters, lastMonthToLoad).then(firstOccuranceDate => {

                // If a month is 
                if (firstOccuranceDate != null) {
                    const toMonth = moment(firstOccuranceDate);

                    // Only change months if it's in the future.
                    dispatch(setCurrentMonth(toMonth))
                    
                    const monthElement = getMonthElement(toMonth);

                    // If the month has already loaded, apply filter and remove the loader.
                    // We need a timeout because even though the data is cached, a Promise object is created.
                 
                    var scrollInterval = setInterval(() => {
                        

                        if (monthElement) {
                            removeLoader();
                            if (scrollToMonth(toMonth)) {
                                clearInterval(scrollInterval);
                            }
                        }
                    }, 500);

                } else {
                    // If the month has already loaded, apply filter and remove the loader.
                    // We need a timeout because even though the data is cached, a Promise object is created.
                    setTimeout(() => removeLoader(), 500);
                }
            }).catch(err => {
                console.error(err);
            });
        }
    }
}

export default connect<StateToProps, DispatchToProps>(mapStateToProps, mapDispatchToProps)(FiltersBase);